import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MessageSquare, Calendar, Trash2 } from 'lucide-react';
import api from '../api';
import { format } from 'date-fns';

export default function ContactDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadContact();
    }, [id]);

    const loadContact = async () => {
        try {
            const data = await api.getContacts();
            const found = data.find(c => c.id === id);
            setContact(found);
        } catch (error) {
            console.error('Error loading contact:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this contact submission?')) return;

        try {
            await api.deleteContact(id);
            navigate('/admin', { state: { activeTab: 'contacts' } });
        } catch (error) {
            alert('Error deleting contact: ' + error.message);
        }
    };

    if (loading) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    if (!contact) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h1>Contact not found</h1>
                    <button onClick={() => navigate('/admin', { state: { activeTab: 'contacts' } })} className="btn btn-primary" style={{ marginTop: '20px' }}>
                        Back to Contacts
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="main" style={{ background: 'var(--bg-admin)', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '900px', padding: '40px 24px' }}>
                {/* Back Button */}
                <button
                    onClick={() => navigate('/admin', { state: { activeTab: 'contacts' } })}
                    className="btn btn-ghost"
                    style={{ marginBottom: '24px', padding: '8px 16px' }}
                >
                    <ArrowLeft size={18} /> Back to Contacts
                </button>

                {/* Contact Header Card */}
                <div className="card" style={{ padding: '40px', marginBottom: '24px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '32px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid var(--border)'
                    }}>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '40px',
                                fontWeight: '700',
                                marginBottom: '20px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                                <MessageSquare size={48} />
                            </div>
                            <h1 style={{
                                fontSize: '32px',
                                fontWeight: '700',
                                color: 'var(--text-primary)',
                                marginBottom: '12px'
                            }}>
                                {contact.subject || 'Contact Submission'}
                            </h1>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{
                                    display: 'inline-block',
                                    background: 'var(--bg-secondary)',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-secondary)'
                                }}>
                                    {contact.name}
                                </span>
                                {contact.created_at && (
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: 'var(--bg-secondary)',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: 'var(--text-secondary)'
                                    }}>
                                        <Calendar size={14} />
                                        {format(new Date(contact.created_at), 'MMM d, yyyy h:mm a')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={handleDelete}
                            className="btn btn-secondary"
                            style={{ color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Trash2 size={18} />
                            Delete
                        </button>
                    </div>

                    {/* Contact Information */}
                    <div style={{
                        background: 'var(--bg-secondary)',
                        padding: '28px',
                        borderRadius: '16px',
                        marginBottom: '24px'
                    }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            color: 'var(--text-primary)'
                        }}>
                            Contact Information
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {contact.email && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Mail size={20} color="var(--primary-admin)" />
                                    <a href={`mailto:${contact.email}`} style={{
                                        color: 'var(--primary-admin)',
                                        textDecoration: 'none',
                                        fontSize: '15px',
                                        fontWeight: '500'
                                    }}>
                                        {contact.email}
                                    </a>
                                </div>
                            )}

                            {contact.phone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Phone size={20} color="var(--primary-admin)" />
                                    <a href={`tel:${contact.phone}`} style={{
                                        color: 'var(--text-primary)',
                                        textDecoration: 'none',
                                        fontSize: '15px'
                                    }}>
                                        {contact.phone}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Message Content */}
                    {contact.message && (
                        <div>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '12px',
                                color: 'var(--text-primary)'
                            }}>
                                Message
                            </h3>
                            <div style={{
                                background: 'var(--bg-secondary)',
                                padding: '20px',
                                borderRadius: '12px',
                                fontSize: '15px',
                                lineHeight: '1.7',
                                color: 'var(--text-secondary)',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {contact.message}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

